import { SignageMediaType } from '../../types/signage';

// Playback rules shared by the kiosk preview. The Android app implements the
// same rules (SignageSequencer): keep both in step.
//  - An image is shown once for its duration; a GIF or video plays loopCount
//    full loops, then the next visible item starts.
//  - When only one item is visible it repeats forever, whatever its loopCount.
//  - The playlist cycles forever.

export const IMAGE_DEFAULT_SECONDS = 8;
// A GIF's real loop length is not known in the browser; the preview assumes this.
export const GIF_LOOP_SECONDS_ESTIMATE = 4;

export interface IPlayable {
  id: string;
  mediaType: SignageMediaType;
  durationSeconds?: number;
  loopCount: number;
  enabled: boolean;
}

export interface IPlaybackPosition {
  index: number;
  loop: number; // 1-based loop of the current item that is playing
}

export const visibleItems = <T extends IPlayable>(items: T[]): T[] => items.filter((item) => item.enabled);

export const loopsRequired = (item: IPlayable): number => (item.mediaType === 'image' ? 1 : Math.max(1, item.loopCount));

// Called when loop `position.loop` of the current item has finished.
export const advance = (visible: IPlayable[], position: IPlaybackPosition): IPlaybackPosition => {
  if (visible.length === 0) {
    return { index: 0, loop: 1 };
  }
  const index = position.index % visible.length;
  if (visible.length === 1) {
    return { index: 0, loop: position.loop + 1 };
  }
  if (position.loop < loopsRequired(visible[index])) {
    return { index, loop: position.loop + 1 };
  }
  return { index: (index + 1) % visible.length, loop: 1 };
};

// How long one loop of an item lasts when it is driven by a timer (images, and
// GIFs whose length is estimated). Videos are driven by their `ended` event.
export const timedLoopSeconds = (item: IPlayable): number => {
  if (item.mediaType === 'image') {
    return item.durationSeconds && item.durationSeconds > 0 ? item.durationSeconds : IMAGE_DEFAULT_SECONDS;
  }
  return GIF_LOOP_SECONDS_ESTIMATE;
};

export const playlistNote = (items: IPlayable[], isDefaultStore: boolean): string => {
  if (items.length === 0) return '';
  const visible = visibleItems(items).length;
  if (visible === 0) {
    return isDefaultStore
      ? 'Nothing is visible, so stores without their own items show only the camera and status.'
      : 'Nothing is visible, so this store shows the Default playlist.';
  }
  if (visible === 1) return 'Only one item is visible, so it repeats forever (loop count is ignored).';
  return '';
};

export const formatBytes = (bytes: number): string =>
  bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

export const rulesLabel = (item: IPlayable): string =>
  item.mediaType === 'image'
    ? `${item.durationSeconds ?? IMAGE_DEFAULT_SECONDS} s`
    : `${item.loopCount} ${item.loopCount === 1 ? 'loop' : 'loops'}`;
